import { Spacing } from '@persona-id/design-tokens';
import { TransferOutIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import CreateQueryTemplateModal from 'dashboard/components/AnalyticsQueryContainer/CreateQueryTemplateModal';
import DisabledProductPage from 'dashboard/components/DisabledProductPage';
import { DISABLED_INQUIRIES_ANALYTICS_PAGE } from 'dashboard/components/DisabledProductPage/config';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Permissioned from 'dashboard/components/Permissioned';
import {
  RoutedTabbedSectionCard,
  RoutedTabbedSectionSwitch,
} from 'dashboard/components/TabbedSectionV2';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { getEffectivePeriod } from 'dashboard/lib/utils/period';
import {
  InquiryAnalyticsQueryParams,
  QueryType,
  RelativeTimeSpan,
} from 'dashboard/models/analytics-query-template';
import { Environment } from 'dashboard/models/environment';
import { ExporterFileType } from 'dashboard/models/exporter';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { showMessage } from 'dashboard/redux/actions/messages';
import { DynamicFilterSet, MetricsState } from 'dashboard/redux/reducers/metrics-inquiry';
import { RootState } from 'dashboard/redux/store';
import {
  createAnalyticsQueryTemplate,
  getAnalyticsQueryTemplates,
} from 'dashboard/redux/thunk-actions/analytics-query-templates';
import {
  loadMetricsInquiryMedianCompletion,
  loadMetricsInquiryVolume,
  loadMetricsInquiryVolumeOverTime,
  setGranularities,
} from 'dashboard/redux/thunk-actions/metrics-inquiry';
import DashboardInquiryAnalyticsFunnelRoute from 'dashboard/routes/DashboardInquiryAnalyticsFunnelRoute';
import DashboardInquiryAnalyticsQueryTemplatesRoute from 'dashboard/routes/DashboardInquiryAnalyticsQueryTemplatesRoute';
import DashboardInquiryAnalyticsVolumeRoute from 'dashboard/routes/DashboardInquiryAnalyticsVolumeRoute';
import InquiryAnalyticsExportModal from 'dashboard/views/DashboardInquiryAnalyticsView/InquiryAnalyticsExportModal';
import Button from 'design-system/components/Button';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { Iso8601DateString } from 'lib/types';

// this is the pagination limit, we should display over 250k in modal once count is >= 250k
// if its above 5M, dont let them break it all plz
export const WARN_LARGE_EXPORT_CUTOFF = 250_000;
export const MAX_EXPORT_COUNT_LIMIT = 5_000_000;

export interface DashboardInquiryAnalyticsViewStateProps
  extends Pick<MetricsState, 'granularities'> {}

interface DashboardInquiryAnalyticsViewProps
  extends Pick<MetricsState, 'volume' | 'filters' | 'lastQueryFilters'> {
  currentEnvironment: Environment;
  productFeatureIsDisabled: boolean;
  handleExportData: (exporterFileType: ExporterFileType) => void;
  queriedAt: Iso8601DateString;
  templates: (Template | InquiryTemplate)[];
  dynamicFilters: DynamicFilterSet;
  inquiryTags: Tag[];
}

const ButtonGroup = styled('div')`
  display: flex;
  grid-column-start: 4;
  justify-content: flex-end;
  padding-right: ${Spacing[4]};

  > * + * {
    margin-left: ${Spacing[3]};
  }
`;

const DashboardInquiryAnalyticsView = (
  props: DashboardInquiryAnalyticsViewProps & PropsFromRedux,
) => {
  const {
    currentEnvironment,
    productFeatureIsDisabled,
    handleExportData,
    volume,
    queriedAt,
    filters,
    lastQueryFilters,
    templates,
    dynamicFilters,
    inquiryTags,
    granularities,
  } = props;

  const [showExportModal, { enable: openExportModal, disable: hideExportModal }] = useToggle();

  const [
    showTooLargeExportModal,
    { enable: openTooLargeExportModal, disable: hideTooLargeExportModal },
  ] = useToggle();

  const [
    showCreateQueryTemplateModal,
    { enable: openCreateQueryTemplateModal, disable: hideCreateQueryTemplateModal },
  ] = useToggle();

  const {
    loading: loadingVolumeMetrics,
    error: errorVolumeMetrics,
    callback: loadVolumeMetricsV2,
  } = useApiCallback(
    async (inquiryTemplateIds: string[]) => {
      const overrideFilters = {
        ...filters,
        templateOrInquiryTemplateIds: inquiryTemplateIds,
      };
      const effectivePeriod = getEffectivePeriod(granularities.overview, [
        filters?.createdAtStart,
        filters?.createdAtEnd,
      ]);
      await Promise.all([
        props.setGranularities({
          granularities: {
            ...granularities,
            overview: effectivePeriod,
          },
        }),
        props.loadMetricsInquiryVolume({
          currentEnvironment,
          filters: overrideFilters,
          includeDeltas: true,
        }),
        props.loadMetricsInquiryMedianCompletion({
          currentEnvironment,
          filters: overrideFilters,
        }),
        props.loadMetricsInquiryVolumeOverTime({
          currentEnvironment,
          filters: overrideFilters,
          overviewPeriod: effectivePeriod,
        }),
      ]);
    },
    [currentEnvironment, filters],
  );

  useApiErrorHandler({
    error: errorVolumeMetrics,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load inquiry metrics.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  // Must reference the URL to be sure that we only do the exports on Inquiry Overview to start
  const location = useLocation();

  const {
    loading: _loadingQueryTemplates,
    error: errorQueryTemplates,
    callback: getQueryTemplates,
  } = useApiCallback(async () => {
    await props.getAnalyticsQueryTemplates();
  }, []);

  useApiErrorHandler({
    error: errorQueryTemplates,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load saved query.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const {
    loading: _createQueryTemplateLoading,
    error: createQueryTemplateError,
    callback: createQueryTemplate,
  } = useApiCallback(
    async (name: string, relativeTimeSpan: RelativeTimeSpan) => {
      await props.createAnalyticsQueryTemplate({
        name,
        relativeTimeSpan,
        query: {
          type: QueryType.Inquiries,
          'template-ids': filters.templateOrInquiryTemplateIds,
          'inquiry-template-ids': filters.templateOrInquiryTemplateIds,
          'country-codes': filters.countryCodes as string[],
          'device-groups': filters.deviceGroups,
          'group-by': filters.groupBy,
          'tag-ids': filters.inquiryTagIds,
          platforms: filters.platforms,
        } as InquiryAnalyticsQueryParams,
      });
      hideCreateQueryTemplateModal();
      props.showMessage('Query saved successfully.', MessageType.Success);
    },
    [filters, hideCreateQueryTemplateModal, props.showMessage],
  );

  useApiErrorHandler({
    error: createQueryTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load inquiry metrics.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const WARNING_MODAL_COPY = (
    <>
      You are exporting over {WARN_LARGE_EXPORT_CUTOFF.toLocaleString()} inquiries. This may take a
      long time. We recommend limiting the amount of inquiries exported by setting filters.
    </>
  );

  const loading = loadingVolumeMetrics;
  const error = errorVolumeMetrics != null || createQueryTemplateError != null;

  const volumeProps = {
    ...props,
    loadVolumeMetricsV2,
    loading,
    error,
    openCreateQueryTemplateModal,
  };

  const estimatedDateRangeIntervalInDays = useMemo(() => {
    const { createdAtStart, createdAtEnd } = filters;
    const start = createdAtStart
      ? DateTime.fromISO(createdAtStart).startOf('day')
      : DateTime.local().startOf('day');
    const end = createdAtEnd
      ? DateTime.fromISO(createdAtEnd).endOf('day')
      : DateTime.local().endOf('day');
    return Math.floor(end.diff(start, 'days').days);
  }, [filters]);

  useEffect(() => {
    getQueryTemplates();
  }, []);

  if (productFeatureIsDisabled) {
    return (
      <DisabledProductPage
        id={DISABLED_INQUIRIES_ANALYTICS_PAGE.id}
        title={DISABLED_INQUIRIES_ANALYTICS_PAGE.title}
        headerTitle={DISABLED_INQUIRIES_ANALYTICS_PAGE.headerTitle}
        description={DISABLED_INQUIRIES_ANALYTICS_PAGE.description}
        uses={DISABLED_INQUIRIES_ANALYTICS_PAGE.uses}
        image={DISABLED_INQUIRIES_ANALYTICS_PAGE.image}
      />
    );
  }

  // Simplification of logic
  const isUnderLimit = volume && volume.created < MAX_EXPORT_COUNT_LIMIT;
  const isExportDisabled =
    location.pathname !== '/dashboard/inquiry-analytics/volume' ||
    queriedAt == null ||
    (volume != null && volume.created === 0) ||
    loadingVolumeMetrics;

  const onHandleExportClick = () => {
    isUnderLimit ? openExportModal() : openTooLargeExportModal();
  };

  return (
    <Container>
      <InactiveEnvironmentBanner />
      <Header title='Analytics'>
        <ButtonGroup>
          <Permissioned permission={Permission.InquiriesExport}>
            <Button
              id='inquiries-analytics__export'
              data-test='inquiries-analytics__export'
              variant='basic'
              // If its above the large export threshold, still allow an export
              // If its above the prevent threshold (5M, it wont finish), ask them to change filters
              onClick={onHandleExportClick}
              disabled={isExportDisabled}
              prefixIcon={<TransferOutIcon />}
            >
              Export
            </Button>
          </Permissioned>
        </ButtonGroup>
      </Header>
      <Section>
        <ModalWrapper isOpen={showTooLargeExportModal} handleCloseModal={hideTooLargeExportModal}>
          <ConfirmationModal
            confirmAction={hideTooLargeExportModal}
            cancelAction={hideTooLargeExportModal}
            header='Export size limit exceeded'
            confirmActionText='Revise filters'
            cancelActionText='Cancel'
          >
            You are attempting to export{' '}
            <strong>over {MAX_EXPORT_COUNT_LIMIT.toLocaleString()}</strong> inquiries. This is more
            than the allowable number of objects to export. Please change the filters and try again.
          </ConfirmationModal>
        </ModalWrapper>
        <ModalWrapper isOpen={showExportModal} handleCloseModal={hideExportModal}>
          <InquiryAnalyticsExportModal
            confirmAction={handleExportData}
            cancelAction={hideExportModal}
            dynamicFilters={dynamicFilters}
            templates={templates}
            inquiryTags={inquiryTags}
            lastQueryFilters={lastQueryFilters.overview}
            warningModal={
              volume != null && volume?.created >= WARN_LARGE_EXPORT_CUTOFF
                ? WARNING_MODAL_COPY
                : null
            }
            confirmButtonType={
              volume != null && volume?.created >= WARN_LARGE_EXPORT_CUTOFF ? 'danger' : 'primary'
            }
          />
        </ModalWrapper>
        <ModalWrapper
          isOpen={showCreateQueryTemplateModal}
          handleCloseModal={hideCreateQueryTemplateModal}
        >
          <CreateQueryTemplateModal
            queryType={QueryType.Inquiries}
            estimatedDateRangeIntervalInDays={estimatedDateRangeIntervalInDays}
            cancelAction={hideCreateQueryTemplateModal}
            saveAction={createQueryTemplate}
          />
        </ModalWrapper>
        <RoutedTabbedSectionCard
          tabs={[
            {
              name: 'Overview',
              path: 'volume',
              dataTest: 'inquiry-analytics-view__tab--volume',
            },
            {
              name: 'Funnel',
              path: 'funnel',
              dataTest: 'inquiry-analytics-view__tab--funnel',
            },
            {
              name: 'Saved Queries',
              path: 'templates',
              dataTest: 'inquiry-analytics-view__tab--templates',
            },
          ]}
        />
        <RoutedTabbedSectionSwitch
          tabs={[
            {
              path: 'volume',
              contents: <DashboardInquiryAnalyticsVolumeRoute {...volumeProps} />,
            },
            {
              path: 'funnel',
              contents: <DashboardInquiryAnalyticsFunnelRoute />,
            },
            {
              path: 'templates',
              contents: <DashboardInquiryAnalyticsQueryTemplatesRoute />,
            },
          ]}
        />
      </Section>
    </Container>
  );
};

const mapDispatchToProps = {
  loadMetricsInquiryVolumeOverTime,
  loadMetricsInquiryMedianCompletion,
  loadMetricsInquiryVolume,
  showMessage,
  createAnalyticsQueryTemplate,
  getAnalyticsQueryTemplates,
  setGranularities,
};

const mapStateToProps = (state: RootState) => ({
  granularities: state.metricsInquiry.granularities,
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardInquiryAnalyticsView);
